import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  LayoutDashboard, Factory, FileText, CreditCard, Users, Settings, LogOut,
  Search, Bell, Menu, X, ChevronDown, HardHat, DollarSign, Truck, Building2, Handshake, ShoppingCart, ShieldAlert, Bot, MessageSquare, Mail, Ticket, Car, BellRing, ListChecks, Boxes
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import ChatPanel from '@/components/ChatPanel';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { startDomTranslator, stopDomTranslator } from '@/lib/domTranslator';

const ADMIN_NAV = [
  { path: '/', key: 'dashboard', icon: LayoutDashboard },
  { path: '/production', key: 'production', icon: Factory },
  { path: '/production/impression-3d', key: 'print3d', icon: Boxes },
  { path: '/payments', key: 'payments', icon: CreditCard },
  { path: '/clients', key: 'clients', icon: Building2 },
  { path: '/price-requests', key: 'price_requests', icon: DollarSign },
  { path: '/subcontractors', key: 'subcontractors', icon: Users },
  { path: '/commercials', key: 'commercials', icon: Handshake },
  { path: '/shipments', key: 'shipments', icon: Truck },
  { path: '/quality', key: 'quality', icon: ShieldAlert },
  { path: '/ai-agent', key: 'ai_agent', icon: Bot },
  { path: '/admin-quotes', key: 'admin_quotes', icon: FileText },
  { path: '/mileage', key: 'mileage', icon: Car },
  { path: '/mail', key: 'mail', icon: Mail },
  { path: '/tickets', key: 'tickets', icon: Ticket },
  { path: '/todo', key: 'todo', icon: ListChecks },
];

const SUB_NAV = [
  { path: '/', key: 'my_space', icon: HardHat },
  { path: '/tickets', key: 'tickets', icon: Ticket },
  { path: '/sub/notifications', key: 'notifications', icon: BellRing },
];

const COMMERCIAL_NAV = [
  { path: '/', key: 'my_space_commercial', icon: Handshake },
];

const CLIENT_NAV = [
  { path: '/', key: 'my_space_client', icon: ShoppingCart },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  // Sidebar: drawer-style on <lg (mobile/tablet), persistent w-64/w-16 on lg+.
  // Defaults: closed on mobile, open on desktop. Tracked separately so we can
  // independently collapse the desktop sidebar OR toggle the mobile drawer.
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(min-width: 1024px)').matches;
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeSubs, setActiveSubs] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);

  // Close the mobile drawer automatically on route change.
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  // Poll chat unread count every 10s (for header badge)
  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get('/messages/unread-count');
        setChatUnread(data.total || 0);
      } catch { /* ignore */ }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 10000);
    return () => clearInterval(id);
  }, [user, chatOpen]);

  // Presence heartbeat: send heartbeat every 30s if user is active (page visible + recent activity)
  useEffect(() => {
    if (!user) return;
    let lastActivity = Date.now();
    const onActivity = () => { lastActivity = Date.now(); };
    ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'].forEach(e => window.addEventListener(e, onActivity, { passive: true }));

    const send = async () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastActivity > 60000) return; // idle > 60s
      try { await api.post('/users/heartbeat'); } catch { /* ignore */ }
    };
    send();
    const interval = setInterval(send, 30000);
    return () => {
      clearInterval(interval);
      ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'].forEach(e => window.removeEventListener(e, onActivity));
    };
  }, [user]);

  const isStaff = user && !['subcontractor', 'client', 'commercial'].includes(user.role);
  useEffect(() => {
    if (isStaff && i18n.language === 'en') startDomTranslator();
    else stopDomTranslator();
    return () => stopDomTranslator();
  }, [isStaff, i18n.language]);

  // Admin only: fetch active subcontractors every 20s
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    const fetchActive = async () => {
      try {
        const { data } = await api.get('/users/active-subcontractors');
        setActiveSubs(data);
      } catch { /* ignore */ }
    };
    fetchActive();
    const interval = setInterval(fetchActive, 20000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    const fetchNotifs = async () => {
      try {
        const { data } = await api.get('/notifications');
        setNotifications(data);
      } catch { /* ignore */ }
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults(null); return; }
    try {
      const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
      setSearchResults(data);
      setSearchOpen(true);
    } catch { /* ignore */ }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen overflow-hidden bg-zinc-950" data-testid="app-layout">
      {/* Mobile backdrop — only when the drawer is open on <lg */}
      {mobileNavOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setMobileNavOpen(false)}
          data-testid="mobile-nav-backdrop"
          aria-hidden="true"
        />
      )}
      {/* Sidebar — drawer on <lg, persistent on lg+. */}
      <aside
        className={`nexus-sidebar border-r border-zinc-800 bg-zinc-950/95 lg:bg-zinc-950/80 backdrop-blur-xl flex flex-col
          fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-200 ease-out
          ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:relative lg:translate-x-0 lg:flex-shrink-0 ${sidebarOpen ? 'lg:w-64' : 'lg:w-16'}`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-zinc-800 shrink-0">
          {(sidebarOpen || mobileNavOpen) ? (
            <div className="flex items-center gap-2.5 flex-1 min-w-0">
              <img src={`${process.env.PUBLIC_URL || ''}/prodpilot-logo.png`} alt="ProdPilot" className="w-8 h-8 rounded-sm object-contain" />
              <span className="text-lg font-bold tracking-tight truncate" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
                <span className="text-zinc-50">Prod</span><span className="text-blue-500">Pilot</span>
              </span>
              {/* Mobile close button */}
              <button
                onClick={() => setMobileNavOpen(false)}
                className="lg:hidden text-zinc-500 hover:text-zinc-100 p-1 -mr-1"
                data-testid="close-mobile-nav"
                aria-label={t('admin.header.close_menu')}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <img src={`${process.env.PUBLIC_URL || ''}/prodpilot-logo.png`} alt="ProdPilot" className="w-8 h-8 rounded-sm object-contain mx-auto" />
          )}
        </div>
        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-1 px-2">
            {(user?.role === 'subcontractor' ? SUB_NAV : user?.role === 'commercial' ? COMMERCIAL_NAV : user?.role === 'client' ? CLIENT_NAV : ADMIN_NAV).map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium nexus-nav-item ${
                    isActive
                      ? 'active bg-zinc-800/50 text-amber-500 border-r-2 border-amber-500'
                      : 'text-zinc-400 hover:text-zinc-100'
                  }`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {(sidebarOpen || mobileNavOpen) && <span>{t(`admin.nav.${item.key}`)}</span>}
                </button>
              );
            })}
            {user?.role === 'admin' && (
              <button
                data-testid="nav-settings"
                onClick={() => navigate('/settings')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium nexus-nav-item ${
                  location.pathname === '/settings'
                    ? 'active bg-zinc-800/50 text-amber-500 border-r-2 border-amber-500'
                    : 'text-zinc-400 hover:text-zinc-100'
                }`}
              >
                <Settings className="w-5 h-5 flex-shrink-0" />
                {(sidebarOpen || mobileNavOpen) && <span>{t('admin.nav.settings')}</span>}
              </button>
            )}
          </nav>
        </ScrollArea>
        {/* User info */}
        {(sidebarOpen || mobileNavOpen) && (
          <div className="border-t border-zinc-800 p-4 shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-zinc-800 rounded-sm flex items-center justify-center text-xs font-bold text-amber-500">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-zinc-200 truncate">{user?.name || 'Utilisateur'}</p>
                <p className="text-xs text-zinc-500 truncate">{user?.role || 'employee'}</p>
              </div>
            </div>
          </div>
        )}
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header — compact on mobile (px-3, gap-2), spacious on desktop */}
        <header className="h-16 border-b border-zinc-800 bg-zinc-950/90 backdrop-blur-2xl flex items-center justify-between px-3 sm:px-4 lg:px-6 gap-2 flex-shrink-0" data-testid="app-header">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <button
              data-testid="toggle-sidebar"
              onClick={() => {
                // On <lg the button opens the drawer; on lg+ it collapses the sidebar.
                if (window.matchMedia('(min-width: 1024px)').matches) {
                  setSidebarOpen(!sidebarOpen);
                } else {
                  setMobileNavOpen(true);
                }
              }}
              className="text-zinc-400 hover:text-zinc-100 transition-colors p-1 -ml-1 shrink-0"
              aria-label={t('admin.header.open_menu')}
            >
              <Menu className="w-5 h-5" />
            </button>
            {/* Desktop / tablet search — hidden on <sm where it's replaced by an icon */}
            <div className="relative hidden sm:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                data-testid="global-search"
                placeholder={t('admin.header.search')}
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchResults && setSearchOpen(true)}
                onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
                className="w-40 md:w-56 lg:w-64 pl-9 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder-zinc-600 rounded-sm focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
              />
              {searchOpen && searchResults && (
                <div className="absolute top-full left-0 mt-1 w-96 max-w-[95vw] bg-zinc-900 border border-zinc-800 rounded-sm shadow-xl z-50 max-h-[70vh] overflow-auto" data-testid="search-results">
                  {(searchResults.results || []).length === 0 ? (
                    <p className="p-4 text-sm text-zinc-500 text-center">{t('admin.header.no_results')} « {searchQuery} »</p>
                  ) : (
                    <>
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium px-3 pt-2">
                        {(searchResults.results || []).length} {(searchResults.results || []).length > 1 ? t('admin.header.results_count_plural') : t('admin.header.results_count')}
                      </p>
                      {(searchResults.results || []).map((r, i) => {
                        const typeMeta = {
                          price_request:   { labelKey: 'price_request',  cls: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30' },
                          price_response:  { labelKey: 'price_response', cls: 'bg-blue-500/10 text-blue-300 border-blue-500/30' },
                          project:         { labelKey: 'project',        cls: 'bg-amber-500/10 text-amber-300 border-amber-500/30' },
                          quote:           { labelKey: 'quote',          cls: 'bg-violet-500/10 text-violet-300 border-violet-500/30' },
                          payment:         { labelKey: 'payment',        cls: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/30' },
                          subcontractor:   { labelKey: 'subcontractor',  cls: 'bg-rose-500/10 text-rose-300 border-rose-500/30' },
                          shipment:        { labelKey: 'shipment',       cls: 'bg-indigo-500/10 text-indigo-300 border-indigo-500/30' },
                        }[r.type] || { labelKey: null, cls: 'bg-zinc-700 text-zinc-300 border-zinc-600' };
                        const label = typeMeta.labelKey ? t(`admin.search_types.${typeMeta.labelKey}`) : r.type;
                        return (
                          <button
                            key={`${r.type}-${r.id}-${i}`}
                            onClick={() => { navigate(r.url); setSearchOpen(false); setSearchQuery(''); setSearchResults(null); }}
                            className="w-full text-left px-3 py-2 hover:bg-zinc-800 flex items-start gap-2 border-t border-zinc-800/50"
                            data-testid={`search-result-${r.type}-${r.id}`}
                          >
                            <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm border ${typeMeta.cls} flex-shrink-0 mt-0.5`}>
                              {label}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-zinc-100 truncate font-medium">{r.title}</p>
                              {r.subtitle && <p className="text-[11px] text-zinc-500 truncate">{r.subtitle}</p>}
                            </div>
                          </button>
                        );
                      })}
                    </>
                  )}
                </div>
              )}
            </div>
            {/* Mobile search icon → opens a full-width sheet */}
            <button
              onClick={() => setMobileSearchOpen(true)}
              className="sm:hidden text-zinc-400 hover:text-zinc-100 p-1.5"
              data-testid="mobile-search-btn"
              aria-label={t('admin.header.search_alt')}
            >
              <Search className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 lg:gap-3 shrink-0">
            {/* Language switcher — persisted on server for the current user */}
            <LanguageSwitcher compact />
            {/* Active subcontractors (admin only) — hidden on <md */}
            {user?.role === 'admin' && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="active-subs-btn" className="hidden md:flex relative text-zinc-400 hover:text-zinc-100 transition-colors px-2 h-9 items-center gap-1.5 bg-zinc-900 border border-zinc-800 rounded-sm">
                    <span className={`w-2 h-2 rounded-full ${activeSubs.length > 0 ? 'bg-emerald-400 animate-pulse' : 'bg-zinc-700'}`} />
                    <span className="text-xs font-medium">{activeSubs.length} {activeSubs.length > 1 ? t('admin.header.active_short_plural') : t('admin.header.active_short_singular')}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72 bg-zinc-900 border-zinc-800 max-h-[60vh] overflow-y-auto">
                  <div className="px-3 py-2 border-b border-zinc-800">
                    <p className="text-sm font-medium text-zinc-200">{t('admin.header.active_subs_online')}</p>
                    <p className="text-[10px] text-zinc-500">{t('admin.header.active_subs_desc')}</p>
                  </div>
                  {activeSubs.length === 0 ? (
                    <p className="px-3 py-4 text-sm text-zinc-500 text-center">{t('admin.header.active_subs_empty')}</p>
                  ) : (
                    activeSubs.map(s => (
                      <div key={s.user_id} data-testid={`active-sub-${s.user_id}`} className="px-3 py-2 border-b border-zinc-800/30 last:border-b-0 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-zinc-200 truncate">{s.company || s.name || s.email}</p>
                          {s.company && (s.name || s.email) && (
                            <p className="text-[10px] text-zinc-500 truncate">{s.name || s.email}</p>
                          )}
                        </div>
                        <span className="text-[10px] text-emerald-400 font-medium">{t('admin.header.active_subs_badge')}</span>
                      </div>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Messages chat */}
            <button
              data-testid="chat-toggle-btn"
              onClick={() => setChatOpen(true)}
              className="relative text-zinc-400 hover:text-zinc-100 transition-colors p-2"
              title={t('admin.header.messages')}
            >
              <MessageSquare className={`w-5 h-5 ${chatUnread > 0 ? 'text-amber-400' : ''}`} />
              {chatUnread > 0 && (
                <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-amber-500 text-zinc-950 text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="chat-unread-badge">
                  {chatUnread > 99 ? '99+' : chatUnread}
                </span>
              )}
            </button>

            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="notifications-btn" className="relative text-zinc-400 hover:text-zinc-100 transition-colors p-2">
                  <Bell className={`w-5 h-5 ${unreadCount > 0 ? 'animate-pulse text-amber-400' : ''}`} />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-rose-500 text-zinc-50 text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notif-badge">
                      {unreadCount}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[calc(100vw-1rem)] sm:w-80 bg-zinc-900 border-zinc-800 max-h-[80vh] overflow-y-auto">
                <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
                  <p className="text-sm font-medium text-zinc-200">{t('admin.header.notifications')}</p>
                  {unreadCount > 0 && (
                    <button onClick={async () => { try { await api.put('/notifications/read-all'); setNotifications(n => n.map(x => ({ ...x, read: true }))); } catch { /* ignore */ } }}
                      data-testid="mark-all-read-btn" className="text-[11px] text-amber-400 hover:text-amber-300 transition-colors">
                      {t('admin.header.mark_all_read')}
                    </button>
                  )}
                </div>
                {notifications.length === 0 ? (
                  <p className="px-3 py-4 text-sm text-zinc-500 text-center">{t('admin.header.notifications_empty')}</p>
                ) : (
                  notifications.slice(0, 10).map(n => (
                    <DropdownMenuItem key={n.id} onClick={async () => {
                      try { await api.put(`/notifications/${n.id}/read`); } catch { /* ignore */ }
                      setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
                      if (n.link) navigate(n.link);
                    }} data-testid={`notif-${n.id}`}
                    className={`px-3 py-2 cursor-pointer focus:bg-zinc-800 border-b border-zinc-800/30 last:border-b-0 ${n.read ? 'text-zinc-500' : 'text-zinc-200 bg-amber-500/5'}`}>
                      <div className="w-full">
                        <div className="flex items-start gap-2">
                          {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{n.title}</p>
                            <p className="text-xs text-zinc-500 truncate">{n.message}</p>
                          </div>
                        </div>
                      </div>
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-btn" className="flex items-center gap-1.5 text-zinc-400 hover:text-zinc-100 transition-colors">
                  <div className="w-8 h-8 bg-zinc-800 rounded-sm flex items-center justify-center text-xs font-bold text-amber-500">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <ChevronDown className="w-4 h-4 hidden sm:inline" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-zinc-200">{user?.name}</p>
                  <p className="text-xs text-zinc-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem data-testid="logout-btn" onClick={handleLogout} className="text-rose-400 focus:bg-zinc-800 focus:text-rose-400">
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('admin.header.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-3 sm:p-4 lg:p-6" data-testid="main-content">
          <div className="nexus-fade-in">
            {children}
          </div>
          <div className="mt-8 pt-4 border-t border-zinc-900 text-[11px] text-zinc-600 flex flex-wrap items-center justify-center gap-3">
            <span>© {new Date().getFullYear()} {t('admin.header.footer_company')}</span>
            <span className="text-zinc-800">·</span>
            <a
              href="/politique-de-confidentialite"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-amber-400 transition-colors"
              data-testid="layout-privacy-link"
            >
              {t('admin.header.privacy')}
            </a>
          </div>
        </main>
      </div>

      {/* Mobile search drawer */}
      <Sheet open={mobileSearchOpen} onOpenChange={setMobileSearchOpen}>
        <SheetContent side="top" className="bg-zinc-950 border-b border-zinc-800 text-zinc-100 p-3" data-testid="mobile-search-drawer">
          <div className="relative mt-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              autoFocus
              placeholder={t('admin.header.search_full')}
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-9 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm focus:ring-1 focus:ring-amber-500"
              data-testid="mobile-search-input"
            />
          </div>
          {searchResults && (
            <div className="mt-3 max-h-[60vh] overflow-auto">
              {(searchResults.results || []).length === 0 ? (
                <p className="p-4 text-sm text-zinc-500 text-center">{t('admin.header.no_results')} « {searchQuery} »</p>
              ) : (
                <>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium px-1 pb-1">
                    {(searchResults.results || []).length} {(searchResults.results || []).length > 1 ? t('admin.header.results_count_plural') : t('admin.header.results_count')}
                  </p>
                  {(searchResults.results || []).map((r, i) => {
                    const typeMeta = {
                      price_request:   { labelKey: 'price_request',  cls: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30' },
                      price_response:  { labelKey: 'price_response', cls: 'bg-blue-500/10 text-blue-300 border-blue-500/30' },
                      project:         { labelKey: 'project',        cls: 'bg-amber-500/10 text-amber-300 border-amber-500/30' },
                      quote:           { labelKey: 'quote',          cls: 'bg-violet-500/10 text-violet-300 border-violet-500/30' },
                      payment:         { labelKey: 'payment',        cls: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/30' },
                      subcontractor:   { labelKey: 'subcontractor',  cls: 'bg-rose-500/10 text-rose-300 border-rose-500/30' },
                      shipment:        { labelKey: 'shipment',       cls: 'bg-indigo-500/10 text-indigo-300 border-indigo-500/30' },
                    }[r.type] || { labelKey: null, cls: 'bg-zinc-700 text-zinc-300 border-zinc-600' };
                    const label = typeMeta.labelKey ? t(`admin.search_types.${typeMeta.labelKey}`) : r.type;
                    return (
                      <button
                        key={`${r.type}-${r.id}-${i}`}
                        onClick={() => { navigate(r.url); setMobileSearchOpen(false); setSearchQuery(''); setSearchResults(null); }}
                        className="w-full text-left px-2 py-2 hover:bg-zinc-800 rounded-sm flex items-start gap-2 border-t border-zinc-800/50"
                        data-testid={`mobile-search-result-${r.type}-${r.id}`}
                      >
                        <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm border ${typeMeta.cls} flex-shrink-0 mt-0.5`}>
                          {label}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-zinc-100 truncate font-medium">{r.title}</p>
                          {r.subtitle && <p className="text-[11px] text-zinc-500 truncate">{r.subtitle}</p>}
                        </div>
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Messages drawer */}
      <Sheet open={chatOpen} onOpenChange={setChatOpen}>
        <SheetContent side="right" className="bg-zinc-950 border-l border-zinc-800 text-zinc-100 p-0 w-full sm:w-[420px] sm:max-w-[420px] flex flex-col" data-testid="chat-drawer">
          <SheetHeader className="px-4 py-3 border-b border-zinc-800 shrink-0">
            <SheetTitle className="flex items-center gap-2 text-zinc-100 text-base">
              <MessageSquare className="w-4 h-4 text-amber-400" /> {t('admin.header.messages')}
              <button
                onClick={() => { setChatOpen(false); navigate('/messages'); }}
                className="ml-auto text-[11px] text-zinc-500 hover:text-amber-400 transition-colors"
                data-testid="open-full-messages-btn"
              >
                {t('admin.header.view_all')}
              </button>
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 min-h-0">
            {chatOpen && <ChatPanel mode="drawer" onUnreadChange={setChatUnread} />}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
